package com.evoapps.evoshift.domain.specialdays

import com.evoapps.evoshift.domain.model.Voivodeship
import com.evoapps.evoshift.domain.model.Voivodeship.DOLNOSLASKIE
import com.evoapps.evoshift.domain.model.Voivodeship.KUJAWSKO_POMORSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.LODZKIE
import com.evoapps.evoshift.domain.model.Voivodeship.LUBELSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.LUBUSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.MALOPOLSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.MAZOWIECKIE
import com.evoapps.evoshift.domain.model.Voivodeship.OPOLSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.PODKARPACKIE
import com.evoapps.evoshift.domain.model.Voivodeship.PODLASKIE
import com.evoapps.evoshift.domain.model.Voivodeship.POMORSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.SLASKIE
import com.evoapps.evoshift.domain.model.Voivodeship.SWIETOKRZYSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.WARMINSKO_MAZURSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.WIELKOPOLSKIE
import com.evoapps.evoshift.domain.model.Voivodeship.ZACHODNIOPOMORSKIE
import com.evoapps.evoshift.domain.model.Workplace

/**
 * SH-227: the voivodeship of a workplace, for its winter break. The one the owner chose in the
 * settings wins; otherwise it is read from the address: the voivodeship's name (OpenStreetMap's
 * suggestions, SH-132, end with "województwo pomorskie" or "Pomeranian Voivodeship"), else a town
 * from [TOWNS] (the cities and the usual resorts). Unknown: no winter break is shown and the
 * setting asks for it. Never guessed from the coordinates (a border is too close too often).
 */
object VoivodeshipFinder {

    /** How the workplace's voivodeship was found. */
    enum class Source { CHOSEN, ADDRESS }

    data class Found(val voivodeship: Voivodeship, val source: Source)

    fun of(workplace: Workplace): Found? =
        workplace.settings.voivodeship?.let { Found(it, Source.CHOSEN) }
            ?: fromAddress(workplace.address)?.let { Found(it, Source.ADDRESS) }

    /** The voivodeship an address names (its name, or else its town), or null. */
    fun fromAddress(address: String?): Voivodeship? {
        if (address.isNullOrBlank()) return null
        val text = " " + words(address) + " "
        // The voivodeship's own name as a whole word: "pomorskie" is not found in "kujawsko-pomorskie".
        Voivodeship.entries.sortedByDescending { it.polishName.length }.firstOrNull { v ->
            " ${fold(v.polishName)} " in text
        }?.let { return it }
        Voivodeship.entries.sortedByDescending { it.englishName.length }.firstOrNull { v ->
            " ${words(v.englishName)} voivodeship " in text || " ${words(v.englishName)} province " in text
        }?.let { return it }
        // A town: the last one named (the street often carries another town's name before it).
        return TOWNS.mapNotNull { (town, voivodeship) ->
            text.lastIndexOf(" $town ").takeIf { it >= 0 }?.let { at -> Triple(at, town.length, voivodeship) }
        }.maxWithOrNull(compareBy<Triple<Int, Int, Voivodeship>> { it.first }.thenBy { it.second })?.third
    }

    /** Lower case without Polish diacritics, words split by anything but letters, digits and hyphens. */
    internal fun words(text: String): String =
        fold(text).map { c -> if (c.isLetterOrDigit() || c == '-') c else ' ' }.joinToString("")
            .split(' ').filter { it.isNotEmpty() }.joinToString(" ")

    private fun fold(text: String): String = text.lowercase().map { c ->
        when (c) {
            'ą' -> 'a'; 'ć' -> 'c'; 'ę' -> 'e'; 'ł' -> 'l'; 'ń' -> 'n'
            'ó' -> 'o'; 'ś' -> 's'; 'ź', 'ż' -> 'z'
            else -> c
        }
    }.joinToString("")

    /** Towns (folded, as [words] writes them) and their voivodeships: the cities and the resorts. */
    internal val TOWNS: List<Pair<String, Voivodeship>> = listOf(
        // Cities with county rights and the larger towns.
        "warszawa" to MAZOWIECKIE, "warsaw" to MAZOWIECKIE, "radom" to MAZOWIECKIE, "plock" to MAZOWIECKIE,
        "siedlce" to MAZOWIECKIE, "ostroleka" to MAZOWIECKIE, "pruszkow" to MAZOWIECKIE, "legionowo" to MAZOWIECKIE,
        "piaseczno" to MAZOWIECKIE, "otwock" to MAZOWIECKIE, "minsk mazowiecki" to MAZOWIECKIE, "ciechanow" to MAZOWIECKIE,
        "krakow" to MALOPOLSKIE, "cracow" to MALOPOLSKIE, "tarnow" to MALOPOLSKIE, "nowy sacz" to MALOPOLSKIE,
        "zakopane" to MALOPOLSKIE, "oswiecim" to MALOPOLSKIE, "nowy targ" to MALOPOLSKIE, "wieliczka" to MALOPOLSKIE,
        "krynica-zdroj" to MALOPOLSKIE, "bochnia" to MALOPOLSKIE, "wadowice" to MALOPOLSKIE,
        "lodz" to LODZKIE, "piotrkow trybunalski" to LODZKIE, "pabianice" to LODZKIE, "tomaszow mazowiecki" to LODZKIE,
        "belchatow" to LODZKIE, "zgierz" to LODZKIE, "skierniewice" to LODZKIE, "sieradz" to LODZKIE, "kutno" to LODZKIE,
        "wroclaw" to DOLNOSLASKIE, "walbrzych" to DOLNOSLASKIE, "legnica" to DOLNOSLASKIE, "jelenia gora" to DOLNOSLASKIE,
        "lubin" to DOLNOSLASKIE, "glogow" to DOLNOSLASKIE, "swidnica" to DOLNOSLASKIE, "karpacz" to DOLNOSLASKIE,
        "szklarska poreba" to DOLNOSLASKIE, "klodzko" to DOLNOSLASKIE, "boleslawiec" to DOLNOSLASKIE,
        "poznan" to WIELKOPOLSKIE, "kalisz" to WIELKOPOLSKIE, "konin" to WIELKOPOLSKIE, "pila" to WIELKOPOLSKIE,
        "ostrow wielkopolski" to WIELKOPOLSKIE, "gniezno" to WIELKOPOLSKIE, "leszno" to WIELKOPOLSKIE,
        "gdansk" to POMORSKIE, "gdynia" to POMORSKIE, "sopot" to POMORSKIE, "slupsk" to POMORSKIE, "tczew" to POMORSKIE,
        "wejherowo" to POMORSKIE, "malbork" to POMORSKIE, "starogard gdanski" to POMORSKIE, "rumia" to POMORSKIE,
        "wladyslawowo" to POMORSKIE, "hel" to POMORSKIE, "jastarnia" to POMORSKIE, "leba" to POMORSKIE, "ustka" to POMORSKIE,
        "puck" to POMORSKIE, "chojnice" to POMORSKIE, "koscierzyna" to POMORSKIE, "jurata" to POMORSKIE,
        "szczecin" to ZACHODNIOPOMORSKIE, "koszalin" to ZACHODNIOPOMORSKIE, "stargard" to ZACHODNIOPOMORSKIE,
        "kolobrzeg" to ZACHODNIOPOMORSKIE, "swinoujscie" to ZACHODNIOPOMORSKIE, "miedzyzdroje" to ZACHODNIOPOMORSKIE,
        "mielno" to ZACHODNIOPOMORSKIE, "darlowo" to ZACHODNIOPOMORSKIE,
        "bydgoszcz" to KUJAWSKO_POMORSKIE, "torun" to KUJAWSKO_POMORSKIE, "wloclawek" to KUJAWSKO_POMORSKIE,
        "grudziadz" to KUJAWSKO_POMORSKIE, "inowroclaw" to KUJAWSKO_POMORSKIE, "ciechocinek" to KUJAWSKO_POMORSKIE,
        "lublin" to LUBELSKIE, "zamosc" to LUBELSKIE, "chelm" to LUBELSKIE, "biala podlaska" to LUBELSKIE,
        "pulawy" to LUBELSKIE, "kazimierz dolny" to LUBELSKIE, "swidnik" to LUBELSKIE,
        "bialystok" to PODLASKIE, "suwalki" to PODLASKIE, "lomza" to PODLASKIE, "augustow" to PODLASKIE,
        "katowice" to SLASKIE, "czestochowa" to SLASKIE, "sosnowiec" to SLASKIE, "gliwice" to SLASKIE, "zabrze" to SLASKIE,
        "bielsko-biala" to SLASKIE, "bytom" to SLASKIE, "rybnik" to SLASKIE, "ruda slaska" to SLASKIE, "tychy" to SLASKIE,
        "dabrowa gornicza" to SLASKIE, "chorzow" to SLASKIE, "jaworzno" to SLASKIE, "jastrzebie-zdroj" to SLASKIE,
        "myslowice" to SLASKIE, "zory" to SLASKIE, "wisla" to SLASKIE, "szczyrk" to SLASKIE, "ustron" to SLASKIE,
        "cieszyn" to SLASKIE, "piekary slaskie" to SLASKIE, "swietochlowice" to SLASKIE,
        "rzeszow" to PODKARPACKIE, "przemysl" to PODKARPACKIE, "stalowa wola" to PODKARPACKIE, "mielec" to PODKARPACKIE,
        "krosno" to PODKARPACKIE, "tarnobrzeg" to PODKARPACKIE, "sanok" to PODKARPACKIE, "jaslo" to PODKARPACKIE,
        "kielce" to SWIETOKRZYSKIE, "ostrowiec swietokrzyski" to SWIETOKRZYSKIE, "starachowice" to SWIETOKRZYSKIE,
        "sandomierz" to SWIETOKRZYSKIE, "skarzysko-kamienna" to SWIETOKRZYSKIE,
        "olsztyn" to WARMINSKO_MAZURSKIE, "elblag" to WARMINSKO_MAZURSKIE, "elk" to WARMINSKO_MAZURSKIE,
        "gizycko" to WARMINSKO_MAZURSKIE, "mikolajki" to WARMINSKO_MAZURSKIE, "ostroda" to WARMINSKO_MAZURSKIE,
        "ilawa" to WARMINSKO_MAZURSKIE, "mragowo" to WARMINSKO_MAZURSKIE, "ketrzyn" to WARMINSKO_MAZURSKIE,
        "opole" to OPOLSKIE, "kedzierzyn-kozle" to OPOLSKIE, "nysa" to OPOLSKIE,
        "zielona gora" to LUBUSKIE, "gorzow wielkopolski" to LUBUSKIE, "nowa sol" to LUBUSKIE, "zary" to LUBUSKIE,
        "swiebodzin" to LUBUSKIE, "zagan" to LUBUSKIE
    )
}
